import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { Globe, ShieldCheck, User } from 'lucide-react';

export type ViewMode = 'customer' | 'admin';

interface HeaderProps {
  viewMode?: ViewMode;
  onSelectViewMode?: (mode: ViewMode) => void;
  onNavigateToAdmin?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  viewMode = 'customer',
  onSelectViewMode,
  onNavigateToAdmin,
}) => {
  const { language, setLanguage } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user.role === 'admin' || user.role === 'superadmin';

  return (
    <header className="sticky top-0 z-30 bg-surface-page/95 backdrop-blur-md border-b border-plum-border px-3 py-2.5 sm:px-4 sm:py-3">
      <div className="max-w-md mx-auto flex items-center justify-between gap-1.5 sm:gap-2">
        {/* Brand */}
        {viewMode === 'admin' && isAdmin ? (
          /* Mobile-fitted Admin Brand (single clean line, zero overflow) */
          <div className="flex items-center space-x-1.5 sm:space-x-2 shrink-0">
            <img
              src="/logos/pluvp-brand.png"
              alt="Pluvp"
              className="h-7 sm:h-8 w-auto max-w-[92px] sm:max-w-[108px] object-contain shrink-0"
            />
            <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded-md bg-primary/20 text-primary-light border border-primary/40 leading-none">
              Admin
            </span>
          </div>
        ) : (
          /* Customer brand */
          <div className="flex flex-col items-start shrink-0">
            <img
              src="/logos/pluvp-brand.png"
              alt="Pluvp"
              className="h-8 w-auto max-w-[108px] object-contain shrink-0"
            />
            <p className="text-[10px] text-plum-muted leading-none mt-0.5 pl-0.5">Data & Airtime</p>
          </div>
        )}

        {/* Right Actions */}
        <div className="flex items-center space-x-1.5 sm:space-x-2 shrink-0">
          {/* Admin Mode Switcher (Visible to authorized admins) */}
          {isAdmin && (
            <div className="flex items-center p-0.5 rounded-xl bg-surface-card border border-plum-border shadow-xs shrink-0">
              <button
                type="button"
                onClick={() => {
                  onSelectViewMode?.('customer');
                }}
                className={`px-2 py-1 text-[10px] sm:text-[11px] font-semibold rounded-lg transition flex items-center space-x-1 ${
                  viewMode === 'customer'
                    ? 'bg-primary text-white shadow-xs'
                    : 'text-plum-muted hover:text-plum-dark'
                }`}
                title="Switch to Customer Mode"
              >
                <User className="w-3 h-3 shrink-0" />
                <span>Customer</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (onSelectViewMode) {
                    onSelectViewMode('admin');
                  } else if (onNavigateToAdmin) {
                    onNavigateToAdmin();
                  }
                }}
                className={`px-2 py-1 text-[10px] sm:text-[11px] font-semibold rounded-lg transition flex items-center space-x-1 ${
                  viewMode === 'admin'
                    ? 'bg-primary text-white shadow-xs'
                    : 'text-plum-muted hover:text-plum-dark'
                }`}
                title="Switch to Admin Mode"
              >
                <ShieldCheck className="w-3 h-3 shrink-0" />
                <span>Admin</span>
              </button>
            </div>
          )}

          {/* Language Toggle */}
          <button
            onClick={() => setLanguage(language === 'my' ? 'en' : 'my')}
            className="p-1.5 rounded-lg border border-plum-border text-plum-muted hover:text-primary hover:border-primary transition text-xs font-semibold flex items-center space-x-1 shrink-0"
            title="Switch Language"
          >
            <Globe className="w-3.5 h-3.5 shrink-0" />
            <span className="text-[11px]">{language === 'my' ? 'MM' : 'EN'}</span>
          </button>
        </div>
      </div>
    </header>
  );
};
